import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { pipe, of } from 'rxjs';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { Area } from 'src/app/model/Area';
import { Availabilities, AvailableSlot } from 'src/app/model/Calendar';
import { DoctorSummary } from 'src/app/model/DoctorSummary';
import { Policy } from 'src/app/model/Policy';
import { PrenotazioneResData } from 'src/app/model/PrenotazioneResData';
import { environment } from 'src/environments/environment';
import * as PrenotazioneActions from '../actions/prenotazione.action';

@Injectable()
export class PrenotazioneEffects {
  fetchAreas$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.fetchAreas),
      switchMap(() => {
        console.log('fetching areas');
        return this.http.get<Area>(environment.NEW_API + '/medical-areas');
      }),
      pipe(
        map((areas) => {
          console.log('areas from effects', areas);
          return PrenotazioneActions.setAreas({ areas: areas.data });
        })
      )
    )
  );

  fetchDocsSumms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.fetchDocsSumms),
      switchMap((action) => {
        let url = environment.NEW_API + '/doctors/summaries';
        if (action.areaId) {
          const filter = {
            filter: {
              filterBy: {
                field: 'medicalArea.id',
                value: action.areaId,
                operation: 'EQ',
              },
            },
          };
          const strinfy = JSON.stringify(filter);
          const base64 = btoa(strinfy);
          url += '?searchRequest=' + base64;
        }
        console.log('fetching doctors');
        return this.http.get<any>(url);
      }),
      pipe(
        map((docsSumms) => {
          console.log('doctors summaries from effects', docsSumms);
          return PrenotazioneActions.setDocsSumms({ docSumms: docsSumms });
        })
      )
    )
  );

  filterDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.filterDocs),
      switchMap((action) => {
        console.log('effects filtering doctors');
        return this.http.get<DoctorSummary>(
          environment.NEW_API +
            '/doctors/summaries?textSearch=' +
            action.textSearch
        );
      }),
      pipe(
        map((docsSumms) => {
          console.log('doctors summaries from effects', docsSumms);
          return PrenotazioneActions.setDocsSumms({ docSumms: docsSumms });
        })
      )
    )
  );

  fetchAvailableAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.fetchAvailableAppointments),
      switchMap((action) => {
        console.log('fetching available appointments');
        return this.http.get<Availabilities>(
          environment.NEW_API +
            '/doctors/' +
            action.doctorId +
            '/availabilities'
        );
      }),
      pipe(
        map((availabilities) => {
          console.log('available appointments from effects', availabilities);
          return PrenotazioneActions.setAllAvailabilities({
            allAvailabilities: availabilities.data,
          });
        })
      )
    )
  );

  fetchAvailableSlots$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.fetchAllSlots),
      switchMap((action) => {
        console.log('fetching available slots');
        return this.http.get<AvailableSlot>(
          environment.NEW_API +
            '/doctors/' +
            action.doctorId +
            '/availabilities/' +
            action.dateId +
            '/slots'
        );
      }),
      pipe(
        map((slots) => {
          console.log('available slots from effects', slots);
          return PrenotazioneActions.setAllSlots({
            slotsAvailable: slots.data,
          });
        })
      )
    )
  );

  fetchPolicies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.fetchPolicies),
      switchMap((action) => {
        return this.http.get<Policy>(environment.NEW_API + '/policies/unread');
      }),
      map((policies) => {
        if (policies) {
          return PrenotazioneActions.fetchPoliciesSuccess({
            policies: policies,
          });
        } else {
          return PrenotazioneActions.fetchPoliciesFail({
            error: 'error: ',
          });
        }
      })
    )
  );

  postPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.postPolicy),
      switchMap((action) => {
        console.log('posting policy');
        return this.http.post<any>(
          environment.NEW_API + '/policies/multiple',
          action.policy
        );
      }),
      map((policies) => {
        console.log('post policies from effects', JSON.stringify(policies));
        if (policies) {
          return PrenotazioneActions.postPolicySuccess();
        } else {
          return PrenotazioneActions.postPolicyFail({
            error: 'error: ',
          });
        }
      })
    )
  );

  postPrenotazione$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.postPrenotazione),
      switchMap((action) => {
        console.log('starting prenotazione');
        return this.http.post<PrenotazioneResData>(
          environment.NEW_API + '/booking-sessions',
          {
            doctorId: action.selectedDoctor,
            medicalSpecializationId: action.medicalSpecializationId,
          }
        );
      }),
      map((data) => {
        console.log('start prenotazione success', data);
        return PrenotazioneActions.prenotazioneResData({
          data: data,
        });
      })
    )
  );

  patchPrenotazione$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.patchPrenotazione),
      switchMap((action) => {
        console.log('patching prenotazione');
        return this.http.patch<PrenotazioneResData>(
          environment.NEW_API + '/booking-sessions/' + action.sessionId,
          {
            doctorSlotId: action.doctorSlotId,
          }
        );
      }),
      map((data) => {
        console.log('prenotazione patched', data);
        return PrenotazioneActions.patchPrenotazioneResData({
          data: data,
        });
      })
    )
  );

  confirmPrenotazione$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrenotazioneActions.confirmPrenotazione),
      switchMap((action) => {
        console.log('confirming prenotazione');
        return this.http.get<any>(
          environment.NEW_API +
            '/booking-sessions/' +
            action.sessionId +
            '/paymentIntent'
        );
      }),
      map((data) => {
        console.log('prenotazione confirmed', data);
        return PrenotazioneActions.prenotazioneSuccess();
      })
    )
  );

  stripeCheckout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PrenotazioneActions.stripeCheckout),
      switchMap((action) => {
        console.log('asking stripe for checkout');
        let body: any = {
          successUrl: action.successUrl,
          cancelUrl: action.cancelUrl,
        };
        if (action.couponCode) {
          body.couponCode = action.couponCode;
        }
        return this.http.post<any>(
          environment.NEW_API +
            '/booking-sessions/' +
            action.sessionId +
            '/checkout',
          body
        );
      }),
      map((data) => {
        console.log('stripe confirmed', data);
        return PrenotazioneActions.stripeCheckoutSuccess({
          checkOutUrl: data,
        });
      }),
      catchError((error) => {
        console.log('stripe error', error);
        return of(PrenotazioneActions.stripeCheckoutFail({ error: error }));
      })
    );
  });

  stripeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PrenotazioneActions.stripeCheckoutSuccess),
        tap((action) => {
          console.log('stripe success', action.checkOutUrl.data.checkOutUrl);
          window.location.href = action.checkOutUrl.data.checkOutUrl;
        })
      ),
    { dispatch: false }
  );

  constructor(private actions$: Actions, private http: HttpClient) {}
}
